import { useTranslation } from "react-i18next";
import {
  OSInputResult,
  SupportedOS,
  useOSCheck,
} from "../../../../hooks/use-os-check";
import { useEffect, useState } from "react";
import PprChipStatus, {
  ChipStatus,
} from "../../../shared/ppr-chip-status/ppr-chip-status";
import { PprItemLayout } from "../../../shared/ppr-item-layout/ppr-item-layout";
import { useBrowserCheck } from "../../../../hooks/use-browser-check";
import { useDeviceCheck } from "../../../../hooks/use-device-check";
import { Flex } from "antd";

export type OperatingSystemCheckOption = {
  listSupportedOS: SupportedOS[];
  osInputResult?: OSInputResult; // Define this if we just to receive the checking result from host app
};

export type SystemCompatibilityCheckOption = {
  operatingSystemCheck?: OperatingSystemCheckOption;
};

const DEFAULT_OPERATING_SYSTEM_CHECK_OPTIONS: OperatingSystemCheckOption = {
  listSupportedOS: [
    {
      name: "Windows",
      maxVersion: Number.MAX_SAFE_INTEGER,
      minVersion: Number.MIN_SAFE_INTEGER,
    },
    {
      name: "MacOS",
      maxVersion: Number.MAX_SAFE_INTEGER,
      minVersion: Number.MIN_SAFE_INTEGER,
    },
  ],
};
interface SystemCompatibilityProps {
  browserCheck: boolean;
  deviceCheck: boolean;
  operatingSystemCheck: boolean;
  systemCompatibilityCheckOption?: SystemCompatibilityCheckOption;
  onComplete: () => void;
  onFail: (
    errors: string[],
    title?: string,
    showFAQLink?: boolean,
    canRetry?: boolean,
  ) => void;
}

const COMPATIBILITY_PREFIX = "SYSTEM_COMPATIBILITY";

const SystemCompatibility = ({
  browserCheck,
  deviceCheck,
  operatingSystemCheck,
  systemCompatibilityCheckOption,
  onComplete,
  onFail,
}: SystemCompatibilityProps) => {
  const { osCheckStatus, osInfo } = useOSCheck(
    operatingSystemCheck,
    systemCompatibilityCheckOption?.operatingSystemCheck?.listSupportedOS ??
      DEFAULT_OPERATING_SYSTEM_CHECK_OPTIONS.listSupportedOS,
    systemCompatibilityCheckOption?.operatingSystemCheck?.osInputResult,
  );

  const { browserInfo, browserCheckStatus } = useBrowserCheck(
    browserCheck &&
      (osCheckStatus === "passed" ||
        osCheckStatus === "failed" ||
        !operatingSystemCheck),
  );

  const { deviceCheckStatus } = useDeviceCheck(
    deviceCheck &&
      (browserCheck
        ? ["passed", "failed"].includes(browserCheckStatus)
        : !operatingSystemCheck ||
          ["passed", "failed"].includes(osCheckStatus)),
  );

  const { t } = useTranslation("translation", {
    keyPrefix: COMPATIBILITY_PREFIX,
  });

  const [isDone, setIsDone] = useState(false);

  useEffect(() => {
    if (
      (!operatingSystemCheck || ["passed", "failed"].includes(osCheckStatus)) &&
      (!browserCheck || ["passed", "failed"].includes(browserCheckStatus)) &&
      (!deviceCheck || ["passed", "failed"].includes(deviceCheckStatus))
    ) {
      setIsDone(true);
    }
  }, [osCheckStatus, browserCheckStatus, deviceCheckStatus]);

  useEffect(() => {
    if (!isDone) return;

    if (
      (!operatingSystemCheck || osCheckStatus === "passed") &&
      (!browserCheck || browserCheckStatus === "passed") &&
      (!deviceCheck || deviceCheckStatus === "passed")
    ) {
      onComplete();
    } else {
      setIsDone(false);
      const errors = [];
      let title = "";

      if (osCheckStatus === "failed") {
        errors.push(`${COMPATIBILITY_PREFIX}.OPERATING_SYSTEM_CHECK.ERROR_ALERT`);
      }

      if (browserCheckStatus === "failed") {
        errors.push(`${COMPATIBILITY_PREFIX}.BROWSER_CHECK.ERROR_ALERT`);
      }

      if (deviceCheckStatus === "failed") {
        errors.push(`${COMPATIBILITY_PREFIX}.DEVICE_CHECK.ERROR_ALERT`);
      }

      onFail([...errors], title, true, false);
    }
  }, [isDone]);

  const getDesc = (
    status: ChipStatus,
    description: string,
    fullInfo?: string,
  ) => {
    switch (status) {
      case "passed":
      case "failed":
        return `${description}${fullInfo ?? ""}`;
      case "not-started":
        return t("NOT_STARTED_STATUS");
      case "analyzing":
        return t("ANALYSING_STATUS");
      default:
        return undefined;
    }
  };

  return (
    <Flex className="flex-layout" vertical gap={45}>
      {operatingSystemCheck && (
        <PprItemLayout
          title={t("OPERATING_SYSTEM_CHECK.TITLE")}
          desc={getDesc(
            osCheckStatus,
            t("OPERATING_SYSTEM_CHECK.DESCRIPTION"),
            osInfo?.fullInfo,
          )}
          child={<PprChipStatus status={osCheckStatus} />}
        />
      )}
      {browserCheck && (
        <PprItemLayout
          title={t(`BROWSER_CHECK.TITLE`)}
          desc={getDesc(
            browserCheckStatus,
            t("BROWSER_CHECK.DESCRIPTION"),
            `${browserInfo?.name} ${browserInfo?.version}`,
          )}
          child={<PprChipStatus status={browserCheckStatus} />}
        />
      )}
      {deviceCheck && (
        <PprItemLayout
          title={t(`DEVICE_CHECK.TITLE`)}
          desc={getDesc(
            deviceCheckStatus,
            t(`DEVICE_CHECK.DESCRIPTION.${deviceCheckStatus.toUpperCase()}`),
          )}
          child={<PprChipStatus status={deviceCheckStatus} />}
        />
      )}
    </Flex>
  );
};

export default SystemCompatibility;
